import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import {
  Chip,
  EmptyState,
  GlassButton,
  LoadingState,
  PageHeader,
  Pagination,
  useConfirm,
} from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { FeedbackItem, FeedbackListResponse } from '@/lib/types.js';

const PAGE_SIZE = 50;

type Filter = 'unack' | 'all' | 'ack';

const FILTER_LABEL: Record<Filter, string> = {
  unack: '未处理',
  all: '全部',
  ack: '已处理',
};

export function FeedbackPage(): JSX.Element {
  const [data, setData] = useState<FeedbackListResponse | null>(null);
  const [filter, setFilter] = useState<Filter>('unack');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { fmtDateTime } = useFmt();
  const confirm = useConfirm();

  const load = useCallback(async (
    currentFilter: Filter,
    currentSearch: string,
    currentOffset: number,
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(currentOffset));
      if (currentFilter !== 'all') params.set('filter', currentFilter);
      if (currentSearch !== '') params.set('search', currentSearch);
      const res = await request<FeedbackListResponse>(
        `/admin/feedback?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter, search, offset);
  }, [load, filter, search, offset]);

  function changeFilter(next: Filter): void {
    setFilter(next);
    setOffset(0);
  }

  function submitSearch(e: React.FormEvent): void {
    e.preventDefault();
    setSearch(searchInput.trim());
    setOffset(0);
  }

  async function ack(id: string, currentlyAcked: boolean): Promise<void> {
    setBusyId(id);
    try {
      await request(`/admin/feedback/${id}/${currentlyAcked ? 'unack' : 'ack'}`, {
        method: 'POST',
      });
      useToast.getState().show('success', currentlyAcked ? '已标为未处理' : '已标为处理');
      await load(filter, search, offset);
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof ApiError ? err.message : '操作失败',
      );
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id: string): Promise<void> {
    const ok = await confirm({
      title: '删除这条反馈？',
      message: '此操作不可恢复。',
      tone: 'danger',
      confirmLabel: '确认删除',
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await request(`/admin/feedback/${id}`, { method: 'DELETE' });
      useToast.getState().show('success', '已删除');
      await load(filter, search, offset);
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof ApiError ? err.message : '删除失败',
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="用户反馈"
        right={
          data !== null && (
            <span className="text-sm text-slate-400">
              未处理 <span className="text-amber-300 font-semibold">{data.unread_total}</span>
              <span className="mx-2 text-slate-700">·</span>
              当前筛选共 {data.total} 条
            </span>
          )
        }
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5">
          {(['unack', 'all', 'ack'] as Filter[]).map((f) => (
            <Chip key={f} active={filter === f} onClick={(): void => changeFilter(f)}>
              {FILTER_LABEL[f]}
            </Chip>
          ))}
        </div>

        <form onSubmit={submitSearch} className="flex gap-2 flex-1 min-w-[240px]">
          <input
            type="text"
            value={searchInput}
            onChange={(e): void => setSearchInput(e.target.value)}
            placeholder="按内容 / Discord 用户名搜索"
            className="flex-1 h-9 px-3 bg-white/[0.03] border border-white/10 rounded-full text-sm text-slate-100 focus:outline-none focus:border-shark-500"
          />
          <GlassButton type="submit" size="sm" variant="primary">
            搜索
          </GlassButton>
        </form>
      </div>

      {error !== null && (
        <div className="rounded-lg bg-rose-900/30 border border-rose-700/50 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {loading && data === null ? (
          <LoadingState />
        ) : data === null || data.items.length === 0 ? (
          <EmptyState
            title={filter === 'unack' ? '没有未处理的反馈' : '暂无记录'}
            icon={filter === 'unack' ? '🎉' : '💬'}
          />
        ) : (
          data.items.map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              busy={busyId === item.id}
              onToggleAck={(): void => void ack(item.id, item.acknowledged_at !== null)}
              onDelete={(): void => void remove(item.id)}
              fmtDateTime={fmtDateTime}
            />
          ))
        )}
      </div>

      <Pagination
        offset={offset}
        total={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

interface FeedbackCardProps {
  item: FeedbackItem;
  busy: boolean;
  onToggleAck: () => void;
  onDelete: () => void;
  fmtDateTime: (s: string | null | undefined) => string;
}

function FeedbackCard({
  item,
  busy,
  onToggleAck,
  onDelete,
  fmtDateTime,
}: FeedbackCardProps): JSX.Element {
  const acked = item.acknowledged_at !== null;
  return (
    <div
      className={`rounded-lg border p-4 transition-colors ${
        acked
          ? 'border-white/10 bg-white/[0.02] opacity-70'
          : 'border-white/10 bg-white/[0.04]'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-1 flex-wrap">
            <SourceBadge source={item.source} />
            {item.source === 'discord' && item.discord_username !== null && (
              <span className="text-slate-300">@{item.discord_username}</span>
            )}
            {item.user_id !== null && (
              <Link
                to={`/users/${item.user_id}`}
                className="text-shark-300 hover:text-shark-200"
              >
                {item.user_email ?? item.user_id.slice(0, 8)}
              </Link>
            )}
            {item.user_id === null && item.contact_email !== null && (
              <a
                href={`mailto:${item.contact_email}`}
                className="text-slate-300 hover:text-shark-300"
              >
                ✉ {item.contact_email}
              </a>
            )}
            {item.user_id === null && item.contact_email === null && item.source !== 'discord' && (
              <span className="text-slate-600 italic">匿名</span>
            )}
            <span className="text-slate-600">·</span>
            <span>{fmtDateTime(item.created_at)}</span>
            {acked && (
              <>
                <span className="text-slate-600">·</span>
                <span className="text-emerald-400">
                  已处理 {fmtDateTime(item.acknowledged_at)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <GlassButton
            size="sm"
            variant={acked ? 'ghost' : 'primary'}
            onClick={onToggleAck}
            disabled={busy}
          >
            {acked ? '↺ 重新标未处理' : '✓ 标为处理'}
          </GlassButton>
          <GlassButton size="sm" variant="danger" onClick={onDelete} disabled={busy}>
            删除
          </GlassButton>
        </div>
      </div>
      <p className="text-sm text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
        {item.content}
      </p>
    </div>
  );
}

const SOURCE_STYLE: Record<string, { label: string; tone: string }> = {
  discord: { label: 'Discord', tone: 'bg-indigo-900/40 text-indigo-200 border-indigo-700/50' },
  web: { label: 'Web', tone: 'bg-shark-900/40 text-shark-200 border-shark-700/50' },
  desktop: { label: 'Desktop', tone: 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50' },
};

function SourceBadge({ source }: { source: string }): JSX.Element {
  const meta = SOURCE_STYLE[source] ?? {
    label: source,
    tone: 'bg-white/[0.05] text-slate-300 border-white/10',
  };
  return (
    <span
      className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded border ${meta.tone}`}
    >
      {meta.label}
    </span>
  );
}
